import { useEffect, useState, type RefObject } from 'react';
import { Icon } from '../../components/ui/icon';
import type { Issue, TemplateVariable } from './api';

/** Shared template-editor pieces (client alerts tab + global templates modal). Markup from mockups client.js / settings.js. */

export function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Insert text at the caret (port of mockup insertAt). Returns the new value. */
export function insertAtCaret(ta: HTMLTextAreaElement | null, value: string, text: string, set: (v: string) => void): void {
  const start = ta?.selectionStart ?? value.length;
  const end = ta?.selectionEnd ?? value.length;
  const next = value.slice(0, start) + text + value.slice(end);
  set(next);
  requestAnimationFrame(() => {
    if (!ta) return;
    ta.focus();
    const pos = start + text.length;
    ta.setSelectionRange(pos, pos);
  });
}

/** Wrap the selection with a WhatsApp marker (*bold*, _italic_, ~strike~). */
export function wrapSelection(ta: HTMLTextAreaElement | null, value: string, mark: string, set: (v: string) => void): void {
  const start = ta?.selectionStart ?? value.length;
  const end = ta?.selectionEnd ?? value.length;
  const next = value.slice(0, start) + mark + value.slice(start, end) + mark + value.slice(end);
  set(next);
  requestAnimationFrame(() => {
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(start + 1, end + 1);
  });
}

export function VarChips(props: { items: TemplateVariable[]; onPick: (token: string) => void; disabled?: boolean }) {
  return (
    <div className="var-chips">
      {props.items.map((v) => (
        <button
          key={v.name}
          type="button"
          className="var-chip"
          title={v.example ? `${v.description} · ${v.example}` : v.description}
          disabled={props.disabled}
          onClick={() => props.onPick(chipToken(v.name))}
        >
          {`{{${v.name}}}`}
        </button>
      ))}
    </div>
  );
}

/** 'alerts[].fields.x' -> token usable inside {{#each alerts}}: 'fields.x'. */
function chipToken(name: string): string {
  if (name.endsWith('[]')) return `{{#each ${name.slice(0, -2)}}}\n{{/each}}`;
  const i = name.lastIndexOf('[].');
  return `{{${i >= 0 ? name.slice(i + 3) : name}}}`;
}

export function FormatToolbar(props: { onWrap: (mark: string) => void; disabled?: boolean }) {
  return (
    <div className="editor-toolbar" role="toolbar" aria-label="עיצוב טקסט">
      <button type="button" title="מודגש" aria-label="מודגש" disabled={props.disabled} onClick={() => props.onWrap('*')}>
        <b>B</b>
      </button>
      <button type="button" title="נטוי" aria-label="נטוי" disabled={props.disabled} onClick={() => props.onWrap('_')}>
        <i>I</i>
      </button>
      <button type="button" title="קו חוצה" aria-label="קו חוצה" disabled={props.disabled} onClick={() => props.onWrap('~')}>
        <s>S</s>
      </button>
    </div>
  );
}

/** Variables + extra fields + toolbar + textarea. */
export function TemplateEditor(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  lang: 'he' | 'en';
  variables?: TemplateVariable[];
  fields?: TemplateVariable[];
  readOnly?: boolean;
  rows?: number;
  taRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const pick = (token: string) => insertAtCaret(props.taRef.current, props.value, token, props.onChange);
  return (
    <>
      {props.variables?.length ? (
        <div>
          <div className="small muted" style={{ marginBottom: 6 }}>
            משתנים
          </div>
          <VarChips items={props.variables} onPick={pick} disabled={props.readOnly} />
        </div>
      ) : null}
      {props.fields?.length ? (
        <div>
          <div className="small muted" style={{ marginBottom: 6 }}>
            שדות נוספים מהדוח של הסייט
          </div>
          <VarChips items={props.fields} onPick={pick} disabled={props.readOnly} />
        </div>
      ) : null}
      <div>
        <FormatToolbar disabled={props.readOnly} onWrap={(m) => wrapSelection(props.taRef.current, props.value, m, props.onChange)} />
        <textarea
          ref={props.taRef}
          className="textarea"
          id={props.id}
          rows={props.rows ?? 9}
          dir={props.lang === 'en' ? 'ltr' : 'rtl'}
          aria-label={props.label}
          value={props.value}
          readOnly={props.readOnly}
          onChange={(e) => props.onChange(e.target.value)}
        />
      </div>
    </>
  );
}

/**
 * Validator list (`ul.validator`) from the server preview issues — same three checks as the mockup
 * (known variables, report link, length) plus any other server issue.
 */
export function ValidatorList(props: { body: string; issues: Issue[]; alertKey: boolean; loading?: boolean; id?: string }) {
  const { body, issues } = props;
  const max = props.alertKey ? 1000 : 4096;
  const unknown = issues.filter((i) => i.code === 'TEMPLATE_UNKNOWN_VARIABLE');
  const noLink = issues.find((i) => i.code === 'TEMPLATE_NO_REPORT_LINK');
  const long = issues.find((i) => i.code === 'TEMPLATE_LONG' || i.code === 'TEMPLATE_TOO_LONG');
  const handled = new Set(['TEMPLATE_UNKNOWN_VARIABLE', 'TEMPLATE_NO_REPORT_LINK', 'TEMPLATE_LONG', 'TEMPLATE_TOO_LONG', 'TEMPLATE_EMPTY']);
  const others = issues.filter((i) => !handled.has(i.code));
  const empty = !body.trim();

  const items: [boolean, string][] = [
    [unknown.length === 0, unknown.length ? unknown.map((u) => u.message).join(' ') : 'כל המשתנים מוכרים'],
  ];
  if (props.alertKey) items.push([!noLink, noLink ? noLink.message : 'יש קישור לדוח המלא']);
  items.push([
    !empty && !long,
    empty ? 'הנוסח ריק. בלי נוסח לא תישלח הודעה' : long ? long.message : `אורך: ${body.length.toLocaleString('en-US')} תווים (עד ${max.toLocaleString('en-US')})`,
  ]);
  for (const o of others) items.push([false, o.message]);

  return (
    <ul className="validator mt" id={props.id} aria-live="polite" aria-busy={props.loading || undefined}>
      {items.map(([ok, msg], i) => (
        <li key={i} className={ok ? 'pass' : 'fail'}>
          <Icon name={ok ? 'check' : 'x'} />
          <span>{msg}</span>
        </li>
      ))}
    </ul>
  );
}

const SOURCE_HE: Record<string, string> = {
  site: 'מגיע מהסייט',
  client: 'שונה ללקוח הזה',
  parent: 'מגיע מההגדרות של לקוח-העל',
  segment: 'מגיע מהגדרות הענף',
  global: 'מגיע מההגדרות הכלליות',
  builtin: 'מגיע מההגדרות הכלליות',
  default: 'מגיע מההגדרות הכלליות',
};

export function Inherit({ from }: { from: string }) {
  return (
    <span className="inherit">
      <Icon name="inherit" />
      {from}
    </span>
  );
}

export function sourceLabel(src: string): string {
  return SOURCE_HE[src] ?? SOURCE_HE.global!;
}

/** "שונה ללקוח הזה · חזרה לברירת המחדל" or the inherit badge (mockup `.cl-mark`). */
export function ScopeMark(props: { overridden: boolean; inheritText?: string; onReset?: () => void; canReset?: boolean; busy?: boolean }) {
  return (
    <span className="cl-mark">
      {props.overridden ? (
        <>
          <span className="override">שונה ללקוח הזה</span>
          {props.canReset && props.onReset ? (
            <button type="button" className="link small" onClick={props.onReset} disabled={props.busy}>
              חזרה לברירת המחדל
            </button>
          ) : null}
        </>
      ) : (
        <Inherit from={props.inheritText ?? 'מגיע מההגדרות הכלליות'} />
      )}
    </span>
  );
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  return same ? `היום ${fmtTime(iso)}` : `${d.getDate()}.${d.getMonth() + 1} ${fmtTime(iso)}`;
}
